/**
 * Design tokens from Figma — OsirisAI redesign.
 * Source: https://www.figma.com/design/4tjbkxFMS8pRMo46vRNDkD/OsirisAI
 */

export const osirisTokens = {
  bg: {
    page: '#1a1a1a',
    surface: 'rgba(255, 255, 255, 0.031)', // Bg/bg-surface #ffffff08
    header: 'rgba(255, 255, 255, 0.08)', // Bg/bg-section_header #ffffff14
    tertiary: 'rgba(26, 26, 26, 0.36)', // Bg/tertiary #1a1a1a5c
    secondary: 'rgba(255, 255, 255, 0.18)', // Bg/secondary #ffffff2e
    darker: 'rgba(26, 26, 26, 0.60)', // Bg/darker #1a1a1a99
    tooltip: 'rgba(255, 255, 255, 0.73)', // Bg/bg-tooltip #ffffffba
    tableHover: 'rgba(255, 255, 255, 0.18)', // Bg/bg-table-hover #ffffff2e
    menuItemActive: '#4e2fb1', // Bg/bg-menu-item-active
    error: '#dc543f', // Bg/bg-error
    errorHover: '#c53538', // Bg/bg-error-hover
    // destructive popup action (Figma Cancel Swarm 29308:123288): Colors/Status/error/cold-800
    btnDanger: '#a80a28',
    btnDangerHover: '#8f0822',
    active: '#6e44f5', // Bg/bg-active
    btnPrimaryDefault: '#6e44f5', // Bg/bg-btn-primary-default
    btnPrimaryActive: '#4e2fb1', // Bg/bg-btn-primary-active
    iconBtnMapTool: 'rgba(26, 26, 26, 0.80)', // Bg/bg-icn_btn-map_tool-default
    iconToggleActive: 'rgba(255, 255, 255, 0.95)',
  },
  border: {
    primary: 'rgba(255, 255, 255, 0.18)', // Border/primary #ffffff2e
    secondary: 'rgba(255, 255, 255, 0.13)', // Border/secondary #ffffff21
    tertiary: 'rgba(255, 255, 255, 0.031)', // Border/tertiary #ffffff08
    active: '#826cfc', // Border/border-input-active
    lighter: 'rgba(255, 255, 255, 0.28)', // Border/border-input-default #ffffff47
    inputError: '#fa6567', // Border/border-input-error
    invertPrimary: 'rgba(26, 26, 26, 0.28)', // Border/invert-primary #1a1a1a47
    invertSecondary: 'rgba(26, 26, 26, 0.15)', // Border/invert-secondary #1a1a1a26
    invertTertiary: 'rgba(26, 26, 26, 0.06)', // Border/brd-invert-tertiary #1a1a1a0f
    btnSecondary: '#826cfc', // Border/border-btn-secondary-default
    btnMap: 'rgba(255, 255, 255, 0.60)', // Border/border-btn-map #ffffff99
    divSection: '#1a1a1a',
  },
  text: {
    primary: 'rgba(255, 255, 255, 0.92)', // Text/txt-primary #ffffffeb
    secondary: 'rgba(255, 255, 255, 0.75)', // Text/txt-secondary #ffffffbf
    tertiary: 'rgba(255, 255, 255, 0.48)', // Text/txt-tertiary #ffffff7a
    muted: 'rgba(255, 255, 255, 0.36)', // Text/txt-muted #ffffff5c
    success: '#74c87c', // Text/txt-success
    warning: '#ffcc00', // Text/txt-warning
    error: '#fa6567', // Text/txt-error
    active: '#826cfc', // Text/txt-active
    tableLinkActive: '#c4b5fd', // Text/txt-table-link-active
    invertPrimary: '#1a1a1a', // Text/txt-invert-primary
    invertSecondary: 'rgba(26, 26, 26, 0.70)', // Text/txt-invert-secondary #1a1a1ab2
    // status text on the light tooltip surface (Figma tooltip-rich 29523:90568)
    invertSuccess: '#296c30', // Colors/Status/success/700
    invertWarning: '#d5aa00', // color/status/warning-dark
    invertError: '#d01b3a', // Colors/Status/error/cold-700
  },
  icons: {
    primary: 'rgba(255, 255, 255, 0.80)', // Icons/primary #ffffffcc
    secondary: 'rgba(255, 255, 255, 0.75)', // Icons/secondary #ffffffbf
    tertiary: 'rgba(255, 255, 255, 0.48)', // Icons/tertiary #ffffff7a
    muted: 'rgba(255, 255, 255, 0.28)', // Icons/muted #ffffff47
    activeLight: '#826cfc', // Icons/active-light
    success: '#74c87c', // Icons/success
    warning: '#ffcc00', // Icons/warning
    error: '#fa7965', // Icons/error
    info: '#20a6f1', // Icons/info
    invertTertiary: 'rgba(26, 26, 26, 0.60)', // Icons/icn-invert-tertiary #1a1a1a99
  },
  accent: {
    main: '#6e44f5', // color/accent/main
    mid: '#826cfc', // color/accent/mid
    dark: '#4e2fb1', // color/accent/dark
    darker: '#422993', // color/accent/dark 4
    light: '#c4b5fd', // color/accent/light-1 3
  },
  state: {
    land: '#d97706', // Bg/bg-state-land
    stabilize: '#4b9d53', // Bg/bg-state-stabilize
    offline: '#525252', // Bg/bg-state-offline
    // Impersonation is "pay attention", not an error — red is reserved for errors
    // and destructive actions (Figma To Do 28691:28009).
    impersonation: '#ecad00',
  },
  // Color Coding spec (Figma 29324:80050, upd. 2026/08/07): one color per
  // flight command, shared by map markers, mission-planning icons and the
  // live-session action buttons
  command: {
    ready: '#74c87c', // green 400 — ready to fly
    takeoff: '#3ec5c9', // teal 500
    moving: '#7d4fd6', // purple 600 — waypoint / mission travel
    rtl: '#edc12a', // yellow 550
    land: '#eb8324', // orange 600
    hold: '#7287ff', // blue 550
  },
  // Drone-card badges keep a reduced palette so the list stays readable:
  // green ready, blue for any in-flight command, orange manual, red critical
  badge: {
    ready: '#308939', // green 600
    takeoff: '#3ec5c9', // teal 500
    inFlight: '#047ac6', // blue 600
    manual: '#eb8324', // orange 600
    critical: '#a80a28', // red 800
  },
  status: {
    success: '#74c87c', // color/status/success
    successDark: '#4b9d53', // color/status/success-dark
    error: '#fa7965', // color/status/error
    errorDark: '#dc543f', // color/status/error-dark
    warning: '#ffcc00', // color/status/warning
    warningDark: '#d5aa00', // color/status/warning-dark
    info: '#20a6f1', // color/status/info
    orange: '#f59e0b', // color/status/orange
    orangeDark: '#d97706', // color/status/orange-dark
    pink: '#c45aaf', // color/status/pink
  },
  labels: {
    vibrantFuchsia: '#fe3ed8',
    beyondBlue: '#6d40ff',
    spanishSkyBlue: '#00a2e2',
    pureGreen: '#00be13',
    mustardGold: '#ecad00',
    goldDrop: '#f78400',
    scarletFlame: '#ff3b1d',
    fieryRose: '#ff4d77',
    violetPunk: '#9b6ff5',
    shinyBlue: '#536dff',
    manganeseGreen: '#00ad9c',
    wildLime: '#85c94e',
    newGray: '#a3a3a3',
  },
  alpha: {
    light100: 'rgba(26, 26, 26, 0.07)', // alpha/light/100 #1a1a1a12
    light200: 'rgba(26, 26, 26, 0.20)', // alpha/light/200 #1a1a1a33
    light400: 'rgba(26, 26, 26, 0.36)', // alpha/light/400 #1a1a1a5c
    light500: 'rgba(26, 26, 26, 0.48)', // alpha/light/500 #1a1a1a7a
    light600: 'rgba(26, 26, 26, 0.60)', // alpha/light/600 #1a1a1a99
    light800: 'rgba(26, 26, 26, 0.75)', // alpha/light/800 #1a1a1abf
    light900: 'rgba(26, 26, 26, 0.80)', // alpha/light/900 #1a1a1acc
    dark50: 'rgba(255, 255, 255, 0.031)', // alpha/dark/50 #ffffff08
    dark100: 'rgba(255, 255, 255, 0.08)', // alpha/dark/100 #ffffff14
    dark130: 'rgba(255, 255, 255, 0.13)', // alpha/dark/130 #ffffff21
    dark150: 'rgba(255, 255, 255, 0.17)', // alpha/dark/150 #ffffff2b
    dark200: 'rgba(255, 255, 255, 0.18)', // alpha/dark/200 #ffffff2e
    dark300: 'rgba(255, 255, 255, 0.28)', // alpha/dark/300 #ffffff47
    dark400: 'rgba(255, 255, 255, 0.36)', // alpha/dark/400 #ffffff5c
    dark500: 'rgba(255, 255, 255, 0.48)', // alpha/dark/500 #ffffff7a
    dark600: 'rgba(255, 255, 255, 0.60)', // alpha/dark/600 #ffffff99
    dark700: 'rgba(255, 255, 255, 0.73)', // alpha/dark/700 #ffffffba
    dark800: 'rgba(255, 255, 255, 0.75)', // alpha/dark/800 #ffffffbf
    dark900: 'rgba(255, 255, 255, 0.80)', // alpha/dark/900 #ffffffcc
    dark950: 'rgba(255, 255, 255, 0.92)', // alpha/dark/950 #ffffffeb
  },
  fonts: {
    display: '"Tektur", "Open Sans", sans-serif',
    body: '"IBM Plex Sans", "Open Sans", sans-serif',
    mono: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
    sans: '"Open Sans", sans-serif',
  },
  typography: {
    caption: 12, // typography/caption
    body2: 14, // typography/body2
  },
  layout: {
    sidebarWidth: 56,
    headerHeight: 48,
    statusBarHeight: 24,
    sectionHeaderHeight: 32,
  },
} as const;

export type OsirisTokens = typeof osirisTokens;
